/**
 * SortableStepChip — individual pipeline step chip with dnd-kit sortable behavior.
 *
 * Renders in two visual modes:
 *   - Visualizer zone chips: dashed purple border, compact
 *   - Transform zone chips: solid border, step number, settings button
 *
 * Hover-expanding action buttons (drag grip, toggle, settings, remove).
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ToggleLeft, ToggleRight, Trash2, Wrench } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { StepRenderHint } from '@/hooks/useVisualizerStepDetection';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ZoneType = 'head-viz' | 'transform' | 'tail-viz';

export interface SortableStepChipProps {
  hint: StepRenderHint;
  idx: number;
  zoneType: ZoneType;
  stepNumber?: number;
  isSelected: boolean;
  onSelectStep: (stepId: string) => void;
  onOpenSettings: (stepId: string) => void;
  removeTool: (stepId: string) => void;
  toggleToolEnabled: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SortableStepChip: React.FC<SortableStepChipProps> = ({
  hint, idx, zoneType, stepNumber, isSelected, onSelectStep, onOpenSettings, removeTool, toggleToolEnabled,
}) => {
  const { t } = useTranslation('pipeline');
  const { step, tool } = hint;
  const label = step.label || tool?.name || step.toolId;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  const handleClick = () => {
    onSelectStep(step.id);
  };

  // Visualizer zone chips: dashed purple border, name-only by default
  if (zoneType === 'head-viz' || zoneType === 'tail-viz') {
    return (
      <React.Fragment>
        {idx > 0 && (
          <span className="text-violet-400/40 text-[9px] tracking-[2px] shrink-0">···</span>
        )}
        <div
          ref={setNodeRef}
          style={style}
          className={cn(
            'group flex items-center px-2 py-0.5 rounded-[7px] text-[10px] cursor-pointer',
            'transition-all duration-150 min-w-0 border border-dashed',
            'border-violet-400/35 bg-violet-400/[0.04] text-violet-400',
            isSelected && 'ring-1 ring-violet-400/40',
            !step.isEnabled && 'opacity-40'
          )}
          onClick={handleClick}
          title={`${zoneType === 'head-viz' ? t('steps.inputView') : t('steps.outputView')}: ${label}${!step.isEnabled ? ` ${t('steps.disabled')}` : ''}`}
        >
          <span className="font-medium truncate">{label}</span>

          {/* Action buttons — expand the chip on hover */}
          <div
            className={cn(
              'flex items-center gap-1 overflow-hidden transition-all duration-150',
              'max-w-0 opacity-0 group-hover:max-w-[80px] group-hover:opacity-100 group-hover:ml-1.5',
            )}
          >
            <span title={t('steps.dragToReorder')} className="shrink-0 flex items-center">
              <GripVertical
                className="h-2.5 w-2.5 text-muted-foreground cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              />
            </span>
            <button
              className="h-3 w-3 rounded-sm hover:text-violet-300 flex items-center justify-center shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleToolEnabled(step.id);
              }}
              aria-label={`${step.isEnabled ? t('steps.disable') : t('steps.enable')} ${label}`}
              title={`${step.isEnabled ? t('steps.disable') : t('steps.enable')} ${label}`}
            >
              {step.isEnabled ? (
                <ToggleRight className="h-2.5 w-2.5" />
              ) : (
                <ToggleLeft className="h-2.5 w-2.5" />
              )}
            </button>
            <button
              className="h-3 w-3 rounded-sm hover:text-rose-400 flex items-center justify-center shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                removeTool(step.id);
              }}
              aria-label={`${t('steps.remove')} ${label}`}
              title={`${t('steps.remove')} ${label}`}
            >
              <Trash2 className="h-2 w-2" />
            </button>
          </div>
        </div>
      </React.Fragment>
    );
  }

  // Transform zone chips: solid border, with settings button
  return (
    <React.Fragment>
      {idx > 0 && (
        <span className="text-muted-foreground/40 text-[10px] shrink-0">→</span>
      )}
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'group flex items-center px-2 py-0.5 rounded-[7px] text-[11px] cursor-pointer',
          'transition-all duration-150 min-w-0 border',
          'border-border bg-muted/50',
          isSelected
            ? 'border-violet-400 text-foreground bg-violet-400/[0.06]'
            : 'text-muted-foreground hover:border-zinc-500 hover:text-foreground',
          !step.isEnabled && 'opacity-40'
        )}
        onClick={handleClick}
        title={`${stepNumber ? t('steps.stepNumber', { number: stepNumber }) : t('steps.view')}: ${label}${!step.isEnabled ? ` ${t('steps.disabled')}` : ''}`}
      >
        <span className="font-medium truncate">{label}</span>

        {/* Action buttons — expand the chip on hover */}
        <div
          className={cn(
            'flex items-center gap-1 overflow-hidden transition-all duration-150',
            'max-w-0 opacity-0 group-hover:max-w-[100px] group-hover:opacity-100 group-hover:ml-1.5',
          )}
        >
          <span title={t('steps.dragToReorder')} className="shrink-0 flex items-center">
            <GripVertical
              className="h-2.5 w-2.5 text-muted-foreground cursor-grab active:cursor-grabbing"
              {...attributes}
              {...listeners}
            />
          </span>
          <button
            className="h-3 w-3 rounded-sm hover:text-foreground flex items-center justify-center shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              toggleToolEnabled(step.id);
            }}
            aria-label={`${step.isEnabled ? t('steps.disable') : t('steps.enable')} ${label}`}
            title={`${step.isEnabled ? t('steps.disable') : t('steps.enable')} ${label}`}
          >
            {step.isEnabled ? (
              <ToggleRight className="h-2.5 w-2.5" />
            ) : (
              <ToggleLeft className="h-2.5 w-2.5" />
            )}
          </button>
          <button
            className="h-3 w-3 rounded-sm text-muted-foreground hover:text-violet-400 flex items-center justify-center shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings(step.id);
            }}
            aria-label={`${t('steps.configure')} ${label}`}
            title={t('steps.stepSettings')}
          >
            <Wrench className="h-2.5 w-2.5" />
          </button>
          <button
            className="h-3 w-3 rounded-sm text-muted-foreground hover:text-rose-400 flex items-center justify-center shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              removeTool(step.id);
            }}
            aria-label={`${t('steps.remove')} ${label}`}
            title={`${t('steps.remove')} ${label}`}
          >
            <Trash2 className="h-2 w-2" />
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};
